/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { Header, BrandColors } from '@blend/enterprise-components';
import { css, StyleSheet } from 'aphrodite';
import { map, orderBy, isEmpty } from 'lodash';
import { BlendBridge } from 'blb-types/blendBridge';
import { ThreadItemContainer } from '../../containers/ThreadItem';
import { EmptyThreadSectionContainer } from '../../containers/EmptyThreadSection';
import { SECTION_TYPES } from '../../types';
import { useContext } from 'react';
import { InboxContext } from '../../containers/Inbox/InboxContext';

const styles = StyleSheet.create({
  headerWrapper: {
    ':last-child': {
      marginTop: 20,
    },
  },
  highlightedHeaderWrapper: {
    backgroundColor: BrandColors.CLOUD_30,
    padding: '8px 12px',
  },
  highlightedThreadsWrapper: {
    marginBottom: 0,
    marginTop: 0,
  },
  threadsWrapper: {
    marginBottom: 15,
    marginTop: 15,
  },
  threadWrapper: {
    ':hover': {
      boxShadow: `0 5px 5px 0px ${BrandColors.BOX_SHADOW}`,
      position: 'relative',
    },
    ':not(:last-child)': {
      marginBottom: -1, // prevent double borders between adjacent elements
    },
    border: `1px solid ${BrandColors.CLOUD_50}`,
    cursor: 'pointer',
    overflow: 'hidden',
    transition: '0.2s',
  },
});

export namespace ThreadSection {
  export interface Props {
    header: SECTION_TYPES;
    threads: BlendBridge.API.Thread[];
  }
}

export const ThreadSection: React.FC<ThreadSection.Props> = ({ header, threads }) => {
  const {
    stylingConfig: { highlightSectionHeaders },
  } = useContext(InboxContext);

  const renderThreads = () => {
    return map(orderBy(threads, 'lastRepliedBy.createdAt', 'desc'), thread => (
      <div className={css(styles.threadWrapper)} data-it="threadContainer" key={`thread-${thread.id}`}>
        <ThreadItemContainer thread={thread} />
      </div>
    ));
  };

  const renderHeader = () => {
    if (highlightSectionHeaders) {
      return (
        <div className={css(styles.highlightedHeaderWrapper)}>
          <Header size={5}>{header}</Header>
        </div>
      );
    } else {
      return (
        <div className={css(styles.headerWrapper)}>
          <Header size={4}>{header}</Header>
        </div>
      );
    }
  };

  return (
    <React.Fragment>
      {renderHeader()}
      <div
        className={css(styles.threadsWrapper, highlightSectionHeaders && styles.highlightedThreadsWrapper)}
        data-it={header.toLowerCase().replace(/ /g, '-')}
      >
        {isEmpty(threads) ? <EmptyThreadSectionContainer header={header} /> : renderThreads()}
      </div>
    </React.Fragment>
  );
};
